import { router } from '@inertiajs/react'
import { Check, Eye, EyeOff } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { ThemeEditor } from '@/components/help_center/ThemeEditor'
import { HelpThemePreview } from '@/components/help_center/HelpThemePreview'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import { activeBrandParam } from '@/lib/activeBrand'
import type { HelpCenterSettings, HelpCenterTheme, HelpCenterBranding } from '@/pages/HelpCenter/types'

interface HelpCenterBrandingCardProps {
  settings: HelpCenterSettings
}

export function HelpCenterBrandingCard({ settings }: HelpCenterBrandingCardProps) {
  const { withAccount } = useRouteParams()

  const [heroHeading, setHeroHeading] = useState(settings.hero_heading ?? '')
  const [heroSubtext, setHeroSubtext] = useState(settings.hero_subtext ?? '')
  const [theme, setTheme] = useState<HelpCenterTheme>(settings.theme ?? {})
  const [branding, setBranding] = useState<HelpCenterBranding>(settings.branding ?? {})
  const [showPreview, setShowPreview] = useState(true)

  const save = () => {
    router.patch(
      routes.helpCenterSettings.update.path(withAccount()),
      {
        help_center_setting: {
          hero_heading: heroHeading,
          hero_subtext: heroSubtext,
          theme: theme as unknown as Record<string, string>,
          branding: branding as unknown as Record<string, string | boolean>,
        },
        ...activeBrandParam(),
      },
      { preserveScroll: true },
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setShowPreview(v => !v)}>
          {showPreview ? <><EyeOff className="size-4" /> Hide preview</> : <><Eye className="size-4" /> Show preview</>}
        </Button>
      </div>
      <div className={`grid grid-cols-1 gap-6 ${showPreview ? 'lg:grid-cols-[1fr_30rem]' : ''}`}>
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Homepage</CardTitle>
              <CardDescription>What visitors see at the top of your help center.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Hero heading</Label>
                <Input value={heroHeading} onChange={e => setHeroHeading(e.target.value)} placeholder="How can we help?" />
              </div>
              <div className="space-y-2">
                <Label>Hero subtext</Label>
                <Input value={heroSubtext} onChange={e => setHeroSubtext(e.target.value)} placeholder="Search our guides, or browse by category." />
              </div>
              <div className="space-y-2">
                <Label>Categories subtitle</Label>
                <Input
                  value={branding.categories_subtitle ?? ''}
                  onChange={e => setBranding({ ...branding, categories_subtitle: e.target.value })}
                  placeholder="e.g. Guides covering setup, integrations, and everyday workflows."
                />
                <p className="text-xs text-muted-foreground">Shown under “Browse by category” (Aurora template). Leave blank to show the collection count.</p>
              </div>
            </CardContent>
          </Card>

          <ThemeEditor
            settings={settings}
            theme={theme}
            branding={branding}
            brandPrimary={settings.brand_primary_color}
            brandAccent={settings.brand_accent_color}
            onThemeChange={setTheme}
            onBrandingChange={setBranding}
          />
        </div>

        {showPreview && (
          <div className="lg:sticky lg:top-6 lg:self-start">
            <HelpThemePreview settings={settings} theme={theme} brandPrimary={settings.brand_primary_color} brandAccent={settings.brand_accent_color} heroHeading={heroHeading} heroSubtext={heroSubtext} />
          </div>
        )}
      </div>

      <div className="flex items-center justify-end border-t border-border pt-4">
        <Button onClick={save}><Check className="size-4" /> Save changes</Button>
      </div>
    </div>
  )
}
